// base
import React, { useMemo, useRef } from 'react';

// style
import { StyledStatusChartCard } from './style';

// libraries
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);
const chartColors = [
  '#1A3670',
  '#306CD2',
  '#498BEB',
  '#5FA6F8',
  '#9265C7',
  '#BC9FE5',
  '#E9DFF5',
  '#E3E3E3'
];
export function generateRandomColors(count) {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const baseColor = chartColors[i % chartColors.length];
    colors.push(baseColor);
  }
  return colors;
}
export const StatusChartCard = ({ statusData, isdashboard = false, onClick, ...rest }) => {
  let legendRef = useRef(null);

  const data = useMemo(() => {
    if (!statusData || statusData.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            hoverOffset: 10,
            labels: ['No Data'],
            data: [0],
            backgroundColor: ['#E3E3E3'],
            hoverBackgroundColor: '#B38B3A',
            borderWidth: 0
          }
        ]
      };
    }

    return {
      labels: statusData.map((item) => item.label),
      datasets: [
        {
          hoverOffset: 10,
          labels: statusData.map((item) => item.label),
          data: statusData.map((item) => item.data),
          backgroundColor: generateRandomColors(statusData.length),
          hoverBackgroundColor: '#B38B3A',
          borderWidth: 0
        }
      ]
    };
  }, [statusData]);

  const drawTextPlugin = useMemo(() => {
    return {
      beforeDraw: (chart) => {
        if (!chart || !chart.ctx || !chart.data || !chart.config) return;

        const { ctx, data } = chart;

        ctx.save();
        const xCoor = chart.getDatasetMeta(0).data[0]?.x + (isdashboard ? 3 : 0);
        const yCoor = chart.getDatasetMeta(0).data[0]?.y + (isdashboard ? 5 : 0);

        ctx.fillStyle = '#2F2F35';
        ctx.textAlign = 'center';
        ctx.font = `600 ${isdashboard ? 16 : 24}px Poppins, sans-serif`;
        ctx.textBaseline = 'middle';

        // data.datasets[0].data를 직접 사용하여 합계 계산
        const totalData = data.datasets[0].data.reduce((acc, cur) => acc + cur, 0);

        const text = `Total\n${totalData}`;
        const lines = text.split('\n');
        const lineHeight = isdashboard ? 25 : 40;

        lines.forEach((line, i) => {
          const yOffset = (lines.length - 1) * -0.5 * lineHeight + i * lineHeight;
          ctx.fillText(line, xCoor, yCoor + yOffset);
        });
      },
      afterInit: (chart) => {
        const fitValue = chart.legend.fit;

        chart.legend.fit = function fit() {
          fitValue.bind(chart.legend)();

          let width = '216px';

          return width;
        };
      }
    };
  }, [statusData, isdashboard]);

  const htmlLegendPlugin = {
    id: 'htmlLegend',

    afterUpdate(chart) {
      if (this.ulElement) {
        this.ulElement.remove();
      }

      const ul = document.createElement('ul');
      this.ulElement = ul;

      if (!chart.config.options.isNoEntry) {
        ul.style.cursor = 'pointer';
        ul.scrollTop = 0;
        const items = chart.options.plugins.legend.labels.generateLabels(chart);

        items.forEach((item, index) => {
          const li = document.createElement('li');
          li.style.display = 'flex';
          li.style.alignItems = 'flex-start';
          li.style.minWidth = '0';
          li.style.cursor = 'pointer';

          // 클릭 이벤트 추가
          li.addEventListener('click', () => {
            const [value, ...textParts] = item.text.split(' ').reverse();
            const text = textParts.reverse().join(' ');

            onClick && onClick({ label: text, value });
          });

          const colorBox = document.createElement('div');
          colorBox.style.backgroundColor = item.fillStyle;
          colorBox.style.width = '16px';
          colorBox.style.height = '16px';
          colorBox.style.flexShrink = '0';
          colorBox.style.borderRadius = '50%';
          colorBox.style.marginRight = '12px';
          colorBox.style.marginTop = '2px';

          li.appendChild(colorBox);

          let textArr = item.text;

          const spanLabel = document.createElement('span');
          spanLabel.style.wordBreak = 'break-word';
          spanLabel.appendChild(document.createTextNode(textArr));

          li.appendChild(spanLabel);
          ul.appendChild(li);
        });

        const jsLegend = legendRef.current;
        jsLegend.style.display = '';
        jsLegend.style.alignItems = '';
        jsLegend.appendChild(ul);

        this.ulCreated = true;
      } else {
        const jsLegend = legendRef.current;

        jsLegend.innerHTML = '';

        const li = document.createElement('li');
        const span = document.createElement('span');
        // span.appendChild(
        //   document.createTextNode(`There is no list of companies available to view.`)
        // );
        li.appendChild(span);
        ul.appendChild(li);

        jsLegend.style.display = 'flex';
        jsLegend.style.alignItems = 'center';
        jsLegend.appendChild(ul);
      }
    }
  };

  const options = useMemo(() => {
    return {
      cutout: '60%',
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 2,
      layout: {
        padding: {
          left: 0,
          right: isdashboard ? 150 : 200
        }
      },
      animation: {
        duration: 1000
      },
      plugins: {
        legend: {
          maxHeight: 200,
          position: 'right',
          align: 'center',
          display: false,
          labels: {
            padding: 20,
            borderWidth: 0,
            useBorderRadius: true,
            borderRadius: 1,
            usePointStyle: true,
            textAlign: 'left',
            boxWidth: 200,
            font: {
              size: 10,
              weight: '600',
              family: 'Poppins'
            },
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              const labels = datasets[0].labels;
              const data = datasets[0].data;

              return labels.map((label, i) => ({
                text: `${label} ${data[i]}`,
                fillStyle: datasets[0].backgroundColor[i],
                index: i,
                lineWidth: 0
              }));
            }
          }
        },
        title: {
          display: false
        },
        tooltip: {
          mode: 'index',
          backgroundColor: 'white',
          titleColor: 'black',
          bodyColor: 'black',
          callbacks: {
            label: function (context) {
              const value = context.formattedValue || '';
              const label = context.label;

              return `
              ${value}\r
              ${label}`;
            }
          }
        }
      },
      onClick: (e, activeEls) => {
        let datasetIndex = activeEls[0].datasetIndex;
        let dataIndex = activeEls[0].index;

        const label = e.chart.data.datasets[datasetIndex].labels[dataIndex];
        const value = e.chart.data.datasets[datasetIndex].data[dataIndex];
        onClick && onClick({ label, value });
      }
    };
  }, [isdashboard, onClick]);

  return (
    <StyledStatusChartCard isdashboard={isdashboard ? 'dashboard' : null}>
      <div className="status-chart-wrapper">
        <Doughnut data={data} options={options} plugins={[drawTextPlugin, htmlLegendPlugin]} />
        <div ref={legendRef} className="chart-card-content-legend"></div>
      </div>
    </StyledStatusChartCard>
  );
};
